<template>
  <div class="modal-overlay" @click.self="$emit('cancel')">
    <div class="modal">
      <h3>{{ template ? 'Redigera mall' : 'Ny mall' }}</h3>

      <div class="field-block">
        <label>Namn</label>
        <input v-model="name" class="text-input" placeholder="t.ex. Push A" />
      </div>
      <div class="field-block">
        <label>Anteckning (valfri)</label>
        <input v-model="notes" class="text-input" placeholder="t.ex. bröst/axlar/triceps" />
      </div>

      <div class="field-block">
        <label>Övningar</label>
        <div v-if="rows.length === 0" class="empty">Inga övningar tillagda.</div>
        <div v-for="(row, i) in rows" :key="i" class="ex-row">
          <div class="ex-row-top">
            <span class="ex-num">{{ i + 1 }}</span>
            <span class="ex-name">{{ exerciseName(row.exerciseTypeId) }}</span>
            <div class="ex-row-btns">
              <button @click="move(i, -1)" :disabled="i === 0" title="Upp">↑</button>
              <button @click="move(i, 1)" :disabled="i === rows.length - 1" title="Ner">↓</button>
              <button class="del" @click="rows.splice(i, 1)" title="Ta bort">✕</button>
            </div>
          </div>

          <div class="ex-targets">
            <label><span>Set</span><input type="number" inputmode="numeric" v-model.number="row.targetSets" /></label>
            <label v-if="tracks(row, 'reps')"><span>Reps</span><input type="number" inputmode="numeric" v-model.number="row.targetReps" /></label>
            <label v-if="tracks(row, 'weight')"><span>Vikt (kg)</span><input type="number" inputmode="decimal" step="0.5" v-model.number="row.targetWeight" /></label>
            <label v-if="tracks(row, 'distance')"><span>Distans (m)</span><input type="number" inputmode="decimal" v-model.number="row.targetDistanceMeters" /></label>
            <label v-if="tracks(row, 'duration')"><span>Tid (s)</span><input type="number" inputmode="numeric" v-model.number="row.targetDurationSeconds" /></label>
          </div>

          <!-- Auto-progression from last time in this template -->
          <div class="ex-prog">
            <span class="prog-label">⭡ Auto-öka från förra</span>
            <select v-model="row.progressionMetric" class="prog-metric">
              <option :value="null">Av</option>
              <option v-if="tracks(row, 'weight')" value="weight">Vikt</option>
              <option v-if="tracks(row, 'distance')" value="distance">Distans</option>
              <option v-if="tracks(row, 'duration')" value="duration">Tid</option>
            </select>
            <template v-if="row.progressionMetric">
              <span class="prog-plus">+</span>
              <input type="number" inputmode="decimal" step="0.5" v-model.number="row.progressionAmount" class="prog-amt" />
              <div class="prog-type">
                <button type="button" :class="{ active: row.progressionType !== 'percent' }" @click="row.progressionType = 'absolute'">{{ unit(row) }}</button>
                <button type="button" :class="{ active: row.progressionType === 'percent' }" @click="row.progressionType = 'percent'">%</button>
              </div>
            </template>
          </div>
        </div>

        <div class="add-row">
          <select v-model.number="pickId" class="text-input">
            <option :value="null" disabled>Välj övning att lägga till...</option>
            <option v-for="et in exerciseTypes" :key="et.id" :value="et.id">{{ et.name }}</option>
          </select>
          <button class="btn-add" :disabled="pickId == null" @click="addRow">Lägg till</button>
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn-ghost" @click="$emit('cancel')">Avbryt</button>
        <button class="btn-save" :disabled="!name.trim()" @click="save">Spara mall</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { numOrNull } from '../../services/gymHelpers.js';

export default {
  name: 'TemplateEditorModal',
  props: {
    template: { type: Object, default: null },
    exerciseTypes: { type: Array, default: () => [] },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const name = ref(props.template?.name || '');
    const notes = ref(props.template?.notes || '');
    const rows = ref((props.template?.exercises || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(e => ({
        exerciseTypeId: e.exerciseType?.id ?? e.exerciseTypeId,
        targetSets: e.targetSets, targetReps: e.targetReps, targetWeight: e.targetWeight,
        targetDistanceMeters: e.targetDistanceMeters, targetDurationSeconds: e.targetDurationSeconds,
        progressionMetric: e.progressionMetric ?? null,
        progressionType: e.progressionType ?? 'absolute',
        progressionAmount: e.progressionAmount,
      })));
    const pickId = ref(null);

    function exType(id) { return props.exerciseTypes.find(e => e.id === id); }
    function exerciseName(id) { return exType(id)?.name || '?'; }
    // Which metrics a row's exercise tracks (fallback: weight+reps when flags unknown).
    function tracks(row, metric) {
      const et = exType(row.exerciseTypeId);
      if (!et) return metric === 'weight' || metric === 'reps';
      return { weight: et.tracksWeight, reps: et.tracksReps, distance: et.tracksDistance, duration: et.tracksDuration }[metric];
    }
    function unit(row) {
      return { weight: 'kg', distance: 'm', duration: 's' }[row.progressionMetric] || '';
    }

    function addRow() {
      if (pickId.value == null) return;
      rows.value.push({
        exerciseTypeId: pickId.value, targetSets: 3, targetReps: 8, targetWeight: null,
        targetDistanceMeters: null, targetDurationSeconds: null,
        progressionMetric: null, progressionType: 'absolute', progressionAmount: null,
      });
      pickId.value = null;
    }
    function move(i, dir) {
      const j = i + dir;
      if (j < 0 || j >= rows.value.length) return;
      const arr = rows.value;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    function save() {
      const exercises = rows.value.map((r, i) => ({
        exerciseTypeId: r.exerciseTypeId,
        order: i + 1,
        targetSets: numOrNull(r.targetSets),
        targetReps: numOrNull(r.targetReps),
        targetWeight: numOrNull(r.targetWeight),
        targetDistanceMeters: numOrNull(r.targetDistanceMeters),
        targetDurationSeconds: numOrNull(r.targetDurationSeconds),
        progressionMetric: r.progressionMetric || null,
        progressionType: r.progressionMetric ? (r.progressionType || 'absolute') : null,
        progressionAmount: r.progressionMetric ? numOrNull(r.progressionAmount) : null,
        notes: null,
      }));
      emit('save', { id: props.template?.id ?? null, name: name.value.trim(), notes: notes.value.trim() || null, exercises });
    }

    return { name, notes, rows, pickId, exerciseName, tracks, unit, addRow, move, save };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.55); backdrop-filter: blur(3px);
  display: flex; align-items: flex-end; justify-content: center; z-index: 1300;
}
.modal {
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-bottom: none;
  border-radius: var(--gym-radius-lg) var(--gym-radius-lg) 0 0; padding: 1.25rem;
  width: 100%; max-width: 520px; max-height: 92vh; overflow-y: auto;
  display: flex; flex-direction: column; gap: 0.85rem; box-shadow: var(--gym-shadow);
}
.modal h3 { margin: 0; font-size: 1.2rem; color: var(--gym-text); }
.field-block { display: flex; flex-direction: column; gap: 0.4rem; }
.field-block > label { font-size: 0.85rem; font-weight: 600; color: var(--gym-text-muted); }
.text-input {
  padding: 0.65rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); font-size: 1rem; width: 100%;
  background: var(--gym-surface-2); color: var(--gym-text);
}
.text-input:focus { outline: none; border-color: var(--gym-accent); box-shadow: 0 0 0 3px var(--gym-accent-soft); }
.empty { color: var(--gym-text-faint); font-size: 0.85rem; }

.ex-row { background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius); padding: 0.65rem; margin-bottom: 0.5rem; }
.ex-row-top { display: flex; align-items: center; gap: 0.5rem; }
.ex-num {
  width: 22px; height: 22px; border-radius: 50%; background: var(--gym-accent); color: var(--gym-on-accent);
  font-size: 0.75rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ex-name { flex: 1; font-weight: 600; color: var(--gym-text); font-size: 0.92rem; }
.ex-row-btns { display: flex; gap: 0.2rem; }
.ex-row-btns button {
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm);
  width: 28px; height: 28px; cursor: pointer; color: var(--gym-text-muted);
}
.ex-row-btns button:disabled { opacity: 0.4; cursor: not-allowed; }
.ex-row-btns .del { color: var(--gym-danger); }

.ex-targets { display: flex; gap: 0.5rem; margin-top: 0.5rem; flex-wrap: wrap; }
.ex-targets label { flex: 1 1 0; min-width: 62px; display: flex; flex-direction: column; gap: 0.2rem; }
.ex-targets span { font-size: 0.7rem; color: var(--gym-text-muted); }
.ex-targets input {
  width: 100%; min-width: 0; padding: 0.45rem 0.5rem;
  border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); font-size: 0.9rem;
  background: var(--gym-surface); color: var(--gym-text); -moz-appearance: textfield;
}
.ex-targets input::-webkit-outer-spin-button, .ex-targets input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.ex-targets input:focus { outline: none; border-color: var(--gym-accent); box-shadow: 0 0 0 3px var(--gym-accent-soft); }

.ex-prog {
  display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; margin-top: 0.55rem;
  padding-top: 0.55rem; border-top: 1px dashed var(--gym-border);
}
.prog-label { font-size: 0.72rem; font-weight: 600; color: var(--gym-text-muted); }
.prog-metric {
  padding: 0.35rem 0.45rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm);
  font-size: 0.82rem; background: var(--gym-surface); color: var(--gym-text);
}
.prog-plus { color: var(--gym-text-muted); font-weight: 700; }
.prog-amt {
  width: 60px; min-width: 0; padding: 0.35rem 0.4rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm);
  font-size: 0.85rem; background: var(--gym-surface); color: var(--gym-text); -moz-appearance: textfield;
}
.prog-amt::-webkit-outer-spin-button, .prog-amt::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.prog-type { display: inline-flex; background: var(--gym-surface-3); border-radius: var(--gym-radius-sm); padding: 0.15rem; }
.prog-type button {
  background: none; border: none; padding: 0.25rem 0.55rem; border-radius: 6px; font-size: 0.8rem;
  color: var(--gym-text-muted); cursor: pointer; min-width: 30px;
}
.prog-type button.active { background: var(--gym-accent); color: var(--gym-on-accent); font-weight: 700; }

.add-row { display: flex; gap: 0.5rem; }
.add-row select { flex: 1; min-width: 0; }
.btn-add {
  background: var(--gym-accent); border: none; color: var(--gym-on-accent); border-radius: var(--gym-radius-sm);
  padding: 0 0.9rem; font-size: 0.88rem; font-weight: 700; cursor: pointer; white-space: nowrap;
  transition: background var(--gym-dur) var(--gym-ease);
}
.btn-add:hover:not(:disabled) { background: var(--gym-accent-hover); }
.btn-add:disabled { opacity: 0.5; cursor: not-allowed; }

.modal-actions { display: flex; gap: 0.6rem; }
.btn-ghost {
  flex: 1; background: var(--gym-surface-2); border: 1px solid var(--gym-border); color: var(--gym-text-muted);
  border-radius: var(--gym-radius); padding: 0.75rem; cursor: pointer;
}
.btn-ghost:hover { background: var(--gym-surface-3); color: var(--gym-text); }
.btn-save {
  flex: 2; background: var(--gym-accent); border: none; color: var(--gym-on-accent);
  border-radius: var(--gym-radius); padding: 0.75rem; font-weight: 700; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease);
}
.btn-save:hover:not(:disabled) { background: var(--gym-accent-hover); }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

@media (min-width: 560px) {
  .modal-overlay { align-items: center; }
  .modal { border-radius: var(--gym-radius-lg); border-bottom: 1px solid var(--gym-border); }
}
</style>
